import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, Typography, MenuItem } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = '/match-events/';
const MATCHES_API_URL = '/matches/';
const PLAYERS_API_URL = '/players/';
const TEAMS_API_URL = '/teams/';

const eventTypeOptions = [
  { value: 'GOAL', label: 'Goal' },
  { value: 'ASSIST', label: 'Assist' },
  { value: 'YELLOW_CARD', label: 'Yellow Card' },
  { value: 'RED_CARD', label: 'Red Card' },
  { value: 'SUBSTITUTION', label: 'Substitution' },
  { value: 'PENALTY_SAVED', label: 'Penalty Saved' },
  { value: 'PENALTY_MISSED', label: 'Penalty Missed' },
  { value: 'OWN_GOAL', label: 'Own Goal' },
  { value: 'CLEAN_SHEET', label: 'Clean Sheet' },
  { value: 'SAVE', label: 'Save' },
  { value: 'TACKLE', label: 'Tackle' },
  { value: 'INTERCEPTION', label: 'Interception' },
  { value: 'SHOT_ON_TARGET', label: 'Shot on Target' },
  { value: 'CHANCE_CREATED', label: 'Chance Created' },
  { value: 'PASS_COMPLETED', label: 'Pass Completed' },
];

const EventForm: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [matches, setMatches] = useState<{ id: number; name: string }[]>([]);
  const [players, setPlayers] = useState<{ id: number; name: string }[]>([]);
  const [teams, setTeams] = useState<{ id: number; name: string }[]>([]);
  const [form, setForm] = useState({
    match: '',
    event_type: '',
    minute: '',
    player: '',
    team: '',
    description: '',
  });

  useEffect(() => {
    axios.get(MATCHES_API_URL).then(res => setMatches(res.data)).catch(() => setMatches([]));
    axios.get(PLAYERS_API_URL).then(res => setPlayers(res.data)).catch(() => setPlayers([]));
    axios.get(TEAMS_API_URL).then(res => setTeams(res.data)).catch(() => setTeams([]));
    if (isEdit) {
      setLoading(true);
      axios.get(API_URL + id + '/').then(res => {
        setForm({
          match: res.data.match ? res.data.match.id : '',
          event_type: res.data.event_type || '',
          minute: res.data.minute || '',
          player: res.data.player ? res.data.player.id : '',
          team: res.data.team ? res.data.team.id : '',
          description: res.data.description || '',
        });
      }).catch(() => setError('Failed to load event')).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = { ...form, match: form.match || null, player: form.player || null, team: form.team || null };
      if (isEdit) {
        await axios.put(API_URL + id + '/', payload);
        setSuccess('Event updated successfully');
      } else {
        await axios.post(API_URL, payload);
        setSuccess('Event created successfully');
        setForm({ match: '', event_type: '', minute: '', player: '', team: '', description: '' });
      }
      setTimeout(() => navigate('/football/events'), 1000);
    } catch (err: any) {
      setError('Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxWidth={500} mx="auto" mt={4}>
      <Typography variant="h5" mb={2}>{isEdit ? 'Edit' : 'Create'} Football Event</Typography>
      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField select label="Match" name="match" value={form.match} onChange={handleChange} required fullWidth>
            <MenuItem value="">None</MenuItem>
            {matches.map(match => <MenuItem key={match.id} value={match.id}>{match.name}</MenuItem>)}
          </TextField>
          <TextField select label="Event Type" name="event_type" value={form.event_type} onChange={handleChange} required fullWidth>
            {eventTypeOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
          </TextField>
          <TextField label="Minute" name="minute" value={form.minute} onChange={handleChange} type="number" fullWidth />
          <TextField select label="Player" name="player" value={form.player} onChange={handleChange} fullWidth>
            <MenuItem value="">None</MenuItem>
            {players.map(player => <MenuItem key={player.id} value={player.id}>{player.name}</MenuItem>)}
          </TextField>
          <TextField select label="Team" name="team" value={form.team} onChange={handleChange} fullWidth>
            <MenuItem value="">None</MenuItem>
            {teams.map(team => <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>)}
          </TextField>
          <TextField label="Description" name="description" value={form.description} onChange={handleChange} fullWidth />
          {error && <Typography color="error">{error}</Typography>}
          {success && <Typography color="success.main">{success}</Typography>}
          <Button type="submit" variant="contained" disabled={loading}>{isEdit ? 'Update' : 'Create'}</Button>
        </Stack>
      </form>
    </Box>
  );
};

export default EventForm; 